import { UseQueryOptions } from '@tanstack/react-query';
import { RemoveMarginOracleData } from '@ui/features/Trade/components/RemoveCollateral/hooks/useRemoveMarginOracleData';
import { useDataChainId } from '@ui/hooks/datachain';
import { useQueryOnce } from '@ui/hooks/useQueryOnce';
import { toBigValue, toReadableValue } from '@ui/lib/bignumber';
import { PayablePoolToken } from '@ui/types/token';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useAccount, usePublicClient } from 'wagmi';

import { getTradingPortalFacet } from '../../contract-instances/getTradingPortalFacet';
import { Position } from '../TradingReaderFacet/useGetPositionV2/types';

export const useEstimateRemoveMarginGasLimit = <TSelect = BigNumber>({
  positionHash,
  amount,
  payToken,
  oracleData,
  opts,
}: {
  positionHash: Nullable<Position['id']>;
  amount: Nullable<BigNumber>;
  payToken: PayablePoolToken;
  oracleData: Nullable<RemoveMarginOracleData>;
  opts?: Pick<UseQueryOptions<BigNumber, Error, TSelect>, 'enabled'>;
}) => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  return useQueryOnce({
    meta: {
      type: 'estimateGas',
    },
    queryKey: [
      'TradingPortalFacet.estimateGas.removeMargin',
      address,
      chainId,
      client?.uid,
      positionHash,
      amount,
      payToken.symbol,
      oracleData?.value,
    ],
    queryFn: async () => {
      if (
        isNil(positionHash) ||
        isNil(address) ||
        !client ||
        !amount ||
        !payToken ||
        isNil(oracleData)
      ) {
        throw new Error('Invalid position hash or address, client');
      }
      const contract = getTradingPortalFacet({
        chainId,
        publicClient: client,
      });

      const lvAmount = toBigValue(amount, payToken.decimals);

      const result = await contract.estimateGas.removeMargin(
        [positionHash, lvAmount, oracleData.oracleUpdateData],
        {
          account: address,
          value: oracleData.value,
        },
      );

      if (isNil(result)) return undefined;

      return toReadableValue(result);
    },
    enabled:
      !!address &&
      !!positionHash &&
      !!amount &&
      amount.gt(0) &&
      !!payToken &&
      !isNil(oracleData) &&
      (opts?.enabled ?? true),
  });
};
